<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<script>
import * as d3 from "d3";
import WyUtils from '../../library/wy-utils.js';
import numeral from 'numeral';

d3.hierarchy.prototype.hover = function(isActive, locationPath) {  
  let gNode = d3.select(`g#node-${this.data.uuid}`);
  
  if(isActive) {
    this.isHover = true;
    
    if(this.parent) {
      if(d3.event.target.classList.contains('collapse-image')) {
        d3.select(d3.event.target).attr('href', `${locationPath}/images/flows/icon_tree_collapse_hover.svg`);
      }
      else {
        //document.querySelector("animate#node-hover-animate").beginElement();
        gNode.classed('active', true);
        d3.selectAll(`path#link-${this.data.uuid}`).classed('active', true);        
        // show expand icon
        gNode.select('rect.node-rect').transition().duration(200).attr('width', (this.width + this.data.labelHoverDiff) + this.expandWidth);
        gNode.select('image.expand-image').attr('x', (this.width + this.data.labelHoverDiff)-4).attr('opacity', 1.0);
      }      
    }
  }
  else {
    this.isHover = false;

    d3.selectAll('g.node').classed('active', false);
    d3.selectAll('path.link').classed('active', false);
    d3.selectAll('image.collapse-image').attr('href', `${locationPath}/images/flows/icon_tree_collapse.svg`);

    // hide expand icon
    gNode.select('rect.node-rect').transition().duration(200).attr('width', this.width);
    gNode.select('image.expand-image').attr('x', this.width-4);
    d3.selectAll('image.expand-image').attr('opacity', 0.0);
  }
}

d3.hierarchy.prototype.collapseInit = function() {  
  if(!this.hiddenChildren) this.hiddenChildren = [];
  let hiddenChildrenLength = this.hiddenChildren ? this.hiddenChildren.length : 0;
  let numOfRemoveItems = this.data.regularNodesCount - hiddenChildrenLength - this.data.maxVisibleNodes;
  
  if(this.children && numOfRemoveItems > 0) {
    this.numOfRemoveItems = numOfRemoveItems;
    this.isShowCollapse = false;
    this.children.splice(this.data.maxVisibleNodes, this.numOfRemoveItems).forEach((el, index) => {
      this.hiddenChildren.splice(index, 0, el);
    });
  }
}

d3.hierarchy.prototype.expandOthers = function(pageSize) {
  if(!this.parent.hiddenChildren) this.parent.hiddenChildren = [];

  if(this.parent.hiddenChildren && this.parent.hiddenChildren.length > 0) {
    let hiddenNodeSize = this.parent.hiddenChildren.length;
    let pageIndex = this.parent.data.regularNodesCount - hiddenNodeSize;
    let pageNo = hiddenNodeSize<pageSize ? hiddenNodeSize : pageSize;

    this.parent.hiddenChildren.splice(0, pageNo).forEach((el,index) => {
      this.parent.children.splice(pageIndex+index, 0, el);
    })
    this.parent.isShowCollapse = this.parent.children.length > this.data.maxVisibleNodes;

    return true;
  }
  return false;
}

d3.hierarchy.prototype.collapseOthers = function() {  
  if(!this.parent.hiddenChildren) this.parent.hiddenChildren = [];
  
  let hiddenChildrenLength = this.parent.hiddenChildren ? this.parent.hiddenChildren.length : 0;
  let numOfRemoveItems = this.parent.data.regularNodesCount - hiddenChildrenLength - this.data.maxVisibleNodes;
  if(numOfRemoveItems > 0) {
    this.parent.children.splice(this.data.maxVisibleNodes, numOfRemoveItems).forEach((el, index) => {
      this.parent.hiddenChildren.splice(index, 0, el);
    });

    this.parent.isShowCollapse = false;
  return true;
  }
  return false;
}

export default {
  parent: null,
  options: {},
  tooltip: null,
  treemap: null,
  svgRoot: null,
  svg: null,
  root: null,
  xnumber: 1,

  divID: "",
  divWidth: 0,
  divHeight: 0,

  locationPath: null,

  init(divID, divWidth, divHeight, options, locationPath, parent, xnumber) {
    if(!this.tooltip) {
      this.parent = parent;
      this.divID = divID;
      this.divWidth = divWidth;
      this.divHeight = divHeight;
      this.options = options;
      this.locationPath = locationPath;
      this.xnumber = (xnumber && xnumber > 0 ? xnumber : 1);
      this.drag = d3.drag()
      
      // create tooltip
      this.tooltip = d3.select(".wy-flow-tree-chart").append("div")	
        .attr("class", "tooltip")				
        .style("opacity", 0);
    }
  },

  destroy() {
    this.tooltip = null;
  },

  moveTree(translate, transform) {
    let widthDiff = (this.svg.treeWidth*translate.scale);

    if(this.svg.treeDraggingX == 'R' && (translate.x >= this.options.margin || widthDiff < this.divWidth)) {
      translate.x = this.options.margin;
    }
    else if(this.svg.treeDraggingX == 'L' && (translate.x+widthDiff < this.divWidth * 0.9)) {
      translate.x = transform.x;
    }

    if(this.svg.treeDraggingY == 'D' && (translate.y+(this.svg.treeMinY*translate.scale) > this.divHeight*0.1)) {
      translate.y = transform.y;
    }
    else if(this.svg.treeDraggingY == 'U' && (translate.y+(this.svg.treeMaxY*translate.scale) < this.divHeight*0.9)) {
      translate.y = transform.y;
    }
    
    if(translate.x != transform.x || translate.y != transform.y) {
      this.svg.attr('transform', `translate(${translate.x}, ${translate.y}) scale(${translate.scale})`);
    }
  },

  zoomTree(type) {
    let transform = WyUtils.getTransform(this.svg.node());        
    let scale = transform.scale + (type ? 0.25 : -0.25);
    if(scale > 0.15) {
      this.svg.transition().duration(600).attr('transform', `translate(${transform.x}, ${transform.y}) scale(${scale})`);      
    }
  },

  resetTree() {
    this.svg.transition().duration(200).attr('transform', `translate(${this.options.margin}, ${this.divHeight/2}) scale(1)`);
  },

  updateTree(treeData) {
    let vm = this;

    // remove old svg
    d3.select("#chart").select("svg").remove();
    // add svg
    this.svgRoot = d3.select(this.divID).append("svg")
      .attr("width", this.divWidth)
      .attr("height", this.divHeight)
      .call(this.drag
        .on("start", function() {
          let xy = d3.mouse(this);
          this.dragInfo = {
            lastX: xy[0],
            lastY: xy[1]
          }          
        })
        .on("drag", function() {
          let xy = d3.mouse(this);
          let diffX = xy[0] - this.dragInfo.lastX;
          let diffY = xy[1] - this.dragInfo.lastY;

          let transform = WyUtils.getTransform(vm.svg.node());
          let translate = {
            x: transform.x + diffX,
            y: transform.y + diffY,
            scale: transform.scale
          }

          if(diffX < 0) vm.svg.treeDraggingX = 'L'; else if(diffX > 0) vm.svg.treeDraggingX = 'R';
          if(diffY < 0) vm.svg.treeDraggingY = 'U'; else if(diffY > 0) vm.svg.treeDraggingY = 'D';

          this.dragInfo.lastX = xy[0];
          this.dragInfo.lastY = xy[1];
          vm.moveTree(translate, transform);
        })
        .on("end", function() {
          vm.svg.treeDraggingX = null;
          vm.svg.treeDraggingY = null;
        })
      )
      .on("wheel", () => {
        d3.event.preventDefault();
        d3.event.stopPropagation();
        // scolling        
        let transform = WyUtils.getTransform(this.svg.node());
        let translate = {
          x: transform.x+d3.event.wheelDeltaX/3,
          y: transform.y+d3.event.wheelDeltaY/3,
          scale: transform.scale
        }

        if(d3.event.wheelDeltaX < 0) vm.svg.treeDraggingX = 'L'; else if(d3.event.wheelDeltaX > 0) vm.svg.treeDraggingX = 'R';
        if(d3.event.wheelDeltaY < 0) vm.svg.treeDraggingY = 'U'; else if(d3.event.wheelDeltaY > 0) vm.svg.treeDraggingY = 'D';

        this.moveTree(translate, transform);
      })
      .on("wheel.zoom", null)
      .on("click.zoom", () => {
        //document.body.click();
        this.parent.hideDropdowns();
        
        // de-hightlight node and link paths
        d3.selectAll('g.node').classed('active', false);
        d3.selectAll('path.link').classed('active', false);
      })
      .on("dblclick.zoom", null);

    // svg filters
    let svgDefs = this.svgRoot.append('defs');
    svgDefs.append('filter').attr('id', 'node-hover-shadow').attr('height', '36').append('feDropShadow').attr('id', 'node-hover-feDropShadow').attr('dx', '5').attr('dy', '6').attr('stdDeviation', '2').attr('flood-color', '#000').attr('flood-opacity', '0.4');
    //svgDefs.append('animate').attr('id', 'node-hover-animate').attr('href', '#node-hover-feDropShadow').attr('attributeName', 'flood-opacity').attr('from', '0.2').attr('to', '0.4').attr('dur', '0.5s').attr('fill', 'freeze');
    svgDefs.append('filter').attr('id', 'node-selected-shadow').attr('height', '36').append('feDropShadow').attr('id', 'node-selected-feDropShadow').attr('dx', '0').attr('dy', '0').attr('stdDeviation', '1.5').attr('flood-color', '#053ae8').attr('flood-opacity', '1.0');
    
    this.svg = this.svgRoot.append("g").attr("transform", `translate(${this.options.margin}, ${this.divHeight/2})`);
    this.svg.renderingProcess = 0;

    // declares a tree layout and assigns the size
    this.treemap = d3.tree().size([this.divHeight, this.divWidth]);
    this.treemap.nodeSize([45, 1]);
    /*this.treemap.separation(function(a, b) {
        return 0.5;
    });;*/

    // Assigns parent, children, height, depth
    this.root = d3.hierarchy(treeData, function(d) { return d.children; });
    this.root.x0 = this.divHeight / 2;
    this.root.y0 = 0;

    // post hide nodes    
    this.root.collapseInit();
    this.update(this.root);
  },

  update(source) {
    var vm = this;
    var duration = 750;

    // Assigns the x and y position for the nodes
    var treeData = this.treemap(this.root);
    // Compute the new tree layout.
    
    var nodes = treeData.descendants(),
        links = treeData.descendants().slice(1);
    
    //let maxId = nodes.reduce(function(maxId, el) {return el.id>maxId ? el.id : maxId;}, 0)

    // Normalize for fixed-depth.
    nodes.forEach(function(d){ d.y = d.parent ? (d.parent.y + d.parent.data.maxLabelWidth) + 100 : 0 });
    //nodes.forEach(function(d){ d.y = d.parent ? (d.parent.y + d.parent.data.labelWidth) + 160 : 0 });

    // ****************** Nodes section ***************************
    // Update the nodes...
    var node = this.svg.selectAll('g.node')
        .data(nodes, function(d) { return d.id || (d.id = d.data.uuid); });

    // Enter any new modes at the parent's previous position.
    var nodeEnter = node.enter().append('g')
      .attr("id", function(d) {
        return `node-${d.data.uuid}`;
      })
      .attr("transform", function() {
        return `translate(${source.y0 + (source.width ? source.width + source.expandWidth : source.data.labelWidth)}, ${source.x0})`;
      })
      .on('click', (d) => {
        d3.event.preventDefault();
        d3.event.stopPropagation();
        document.body.click();
        let target = d3.event.target;
        
        if(!this.svg.clicked) {
          // prevent double click trigger
          this.svg.clicked = true;
          setTimeout(() => { this.svg.clicked = false }, 400)

          // close tooltip
          this.tooltip.isOpen = false;
          this.tooltip.transition().duration(50).style("opacity", 0);

          if(this.svg.renderingProcess <= 0) {
            if(target.classList.contains('collapse-image')) {
              if(d.collapseOthers()) this.update(d.parent);
            }
            else if(target.classList.contains('node-rect') || target.classList.contains('node-text') || target.classList.contains('expand-image') ||  target.classList.contains('root-image')) {
              if(d.data.isOther) {
                // expand other nodes
                if(d.expandOthers(this.options.itemsPerExpand)) this.update(d.parent);
              }
              else {
                let ancestors = d.data.ancestors + d.data.label + "||";

                // remember current click
                if(this.svg.previousClickedNode) {
                  this.svg.previousClickedNode.selectedNode = false;  
                }
                
                d.selectedNode = true;
                this.svg.previousClickedNode = d;

                if(!d.children){
                  if(d._children && d._children.length > 0) {
                    d.children = d._children;
                    d._children = null;
                    this.update(d);
                    this.parent.loadNodeMessages(d.data, ancestors);
                  }
                  else if(!d.data.isEndNode) {
                    // fetch next branch and create child nodes                    
                    this.parent.fetchChildren(d.data, ancestors, (items) => {
                      if(items && items.length > 0) {
                        d.children = [];
                        items.forEach(item => {
                          var newChild = d3.hierarchy(item);
                          newChild.depth = item.depth; 
                          newChild.parent = d; 
                          newChild.id = item.uuid;
                          d.children.push(newChild);
                          d.data.children.push(newChild.data);
                        });
                        d.collapseInit();                        
                        this.update(d);
                        this.parent.loadNodeMessages(d.data, ancestors);
                      }
                      
                    });
                  }
                  else {
                    this.parent.loadNodeMessages(d.data, ancestors);
                  }
                }
                else {                  
                  d._children = d.children;
                  d.children = null;
                  this.update(d);
                  this.parent.loadNodeMessages(d.data, ancestors);
                }
              }
            }
          }
        }
      })
      .on("mouseover", (d) => {
        d3.event.preventDefault();
        d3.event.stopPropagation();
        let target = d3.event.target;

        if(!d.isHover && this.svg.renderingProcess <= 0 && !this.tooltip.isOpen == true) {
          // de-hightlight node and link paths
          d.hover(false, vm.locationPath);
          
          if(d.depth > 0) {
            d.ancestors().forEach((el) => {
              el.hover(true, vm.locationPath);
            })
            
            if(target.classList.contains('node-percent')) {
              this.tooltip.isOpen = true;
              this.tooltip.transition().delay(500).duration(300).style("opacity", 0.98);          
              this.tooltip.html(`
                  <div class="title">${d.data.label}</div>
                  <div class="row"><div class="label">% of all messages</div><div class="value">${d.data.countPercentLabel}</div></div>
                  <div class="row"><div class="label">Number of messages</div><div class="value">${d.data.count*this.xnumber}</div></div>
                `)
                .style("left", (d3.event.pageX-220) + "px")		
                .style("top", (d3.event.pageY-150) + "px");
            }
          }
        }
      })
      .on("mouseout", (d) => {
        if(d3.event.toElement && d3.event.toElement.tagName == "svg") {
          d.hover(false, vm.locationPath);

          // hide tooltip
          if(this.tooltip.isOpen == true) {
            this.tooltip.isOpen = false;
            this.tooltip.transition().duration(200).style("opacity", 0);
          }
        }
      });

    // Add rect for the nodes
    nodeEnter.append('rect')
      .attr('class', 'node-rect')
      .attr('rx', function(d){
        d.width = d.data.labelWidth;
        d.expandWidth = 0;
        return d.parent ? 14 : (d.data.labelWidth/2);
      })
      .attr('y', function(d){
        return d.parent ? -(vm.options.nodeHeight/2) : ((d.data.labelWidth/2)*-1);
      });

    // Add labels for the nodes
    nodeEnter.append('text')
      .attr("class", "node-text")
      .attr("dy", ".35em")
      .attr("text-anchor", "start");

    // add root image
    let nodeRoot = nodeEnter.filter(d => d.depth==0);
    nodeRoot.append('image')
      .attr('class', 'root-image')
      .attr('href', function(d) {
        return d.data.label!=vm.parent.defaultIntent ? `${vm.locationPath}/images/flows/icon_tree_intent.svg` : `${vm.locationPath}/images/flows/icon_tree_start.svg`
      })
      .attr('y', -30);

    // add node image
    nodeEnter.filter(d => d.data.image != null).append('image')
      .attr('class', 'node-image')
      .attr('href', function(d) { 
        d.width+= 20;
        return `${vm.locationPath}/images/flows/${d.data.image}`
      })
      .attr('width', 12)
      .attr('height', 12)
      .attr('x', 12)
      .attr('y', -6);

    ///nodeEnter.filter(d => (d.children != null || d._children != null)).append('image')
    nodeEnter.filter(d => !d.data.isEndNode).append('image')
      .attr('class', 'expand-image')
      .attr('href', function(d) {
        d.expandWidth = 20;
        return`${vm.locationPath}/images/flows/icon_tree_plus_${d.data.type=="feedback" ? 'grey' : 'white'}.svg`;
      })
      .attr('opacity', 0.0)
      .attr('width', 16)
      .attr('height', 16)
      .attr('x', function(d) {
        return d.width-4;
      })
      .attr('y', -8);

    // add collapse image
    let nodeOther = nodeEnter.filter(d => d.data.isOther);
    nodeOther.append('image')
      .attr('class', 'collapse-image')
      .attr('href', `${vm.locationPath}/images/flows/icon_tree_collapse.svg`)            
      .attr('opacity', 0.0)
      .attr('width', 100)
      .attr('height', 30)
      .attr('x', 130)      
      .attr('y', -15);

    // Add percent rect
    nodeEnter.filter(d => !d.data.isEndNode).append('rect')
      .attr('class', 'node-percent')
      .attr('fill-opacity', 0.0)
      .attr('rx', 4)
      .attr('x', -53)
      .attr('y', -10);

    // Add percent label
    nodeEnter.filter(d => !d.data.isEndNode).append('text')
      .attr("class", "node-percent-text")
      .attr('opacity', 0.0)
      .attr("dy", ".35em")
      .attr('x', -30)
      .attr("text-anchor", "middle");
    
    // ******************** UPDATE NODES ********************
    var nodeUpdate = nodeEnter.merge(node);
    
    // Transition to the proper position for the node
    nodeUpdate
      .attr('class', (d) => {
        let classNames = `node type-${d.data.type} ${d.data.key} ${d.selectedNode ? 'selectedNode' : ''}`;
        if(d.data.subtype) classNames+= ` ${d.data.subtype}`;
        if(d.data.isCollapsed) classNames+= ' collapsed';
        //if(d.children || d._children) classNames+= ' children';
        return classNames;
      })
      .transition()
      .duration(duration)
      .attr("transform", function(d) {
        return `translate(${d.y}, ${d.x})`;
      })
      .on('start', () => {
        this.svg.renderingProcess++;
      })
      .on('end', () => {
        this.svg.renderingProcess--;
      });

    nodeUpdate.select('rect.node-rect')
      .transition()
      .duration(duration)
      .attr('width', function(d) {
        //return d._children && d.isHover ? (d.width + d.data.labelHoverDiff) + d.expandWidth : d.width;
        return d.width;
      });

    nodeUpdate.select('image.expand-image')
      .transition()
      .duration(duration)
      .attr('opacity', function() {
        //return d._children && d.isHover ? 1.0 : 0.0;
        return 0.0;
      });

    nodeUpdate.select('image.collapse-image')
      .transition()
      .duration(duration)
      .attr('opacity', function(d) {
        return d.parent.isShowCollapse ? 1.0 : 0.0;
      });

    nodeUpdate.select('text.node-text')
      .attr('x', function(d) {
        return d.data.image ? 30 : 10;
      })
      .transition()
      .delay(duration * 0.6)
      .text(function(d) {
        if(d.data.isOther) {
          let otherCount = d.parent.hiddenChildren ? d.parent.hiddenChildren.length : 0;
          return `${d.data.label} (${otherCount})`;
        }
        else  {
          return d.parent ? d.data.label : ''; 
        }
      });

    nodeUpdate.select('rect.node-percent')
      .attr('width', 46)
      .attr('height', 20)
      .transition()
      .delay(duration * 0.6)
      .attr('fill-opacity', function(d) {
        return d.parent ?  0.9 : 0.9;
      });

    nodeUpdate.select('text.node-percent-text')
      .text(function(d) {
        if(d.data.isOther) {
          let otherCount = 0;
          let otherPerent = 0;
          if(d.parent.hiddenChildren) {
            otherCount = d.parent.hiddenChildren.reduce((total, child) => { return total+child.data.count }, 0);
            otherPerent = d.parent.hiddenChildren.reduce((total, child) => { return total+child.data.countPercent }, 0);
          }
          d.data.count = otherCount;
          d.data.countPercent = otherPerent;
        }
        d.data.countPercentLabel = d.parent ? numeral(d.data.countPercent).format('0.00%') : '100%';

        return  d.data.countPercentLabel;
      })
      .transition()
      .delay(duration * 0.6)
      .attr('opacity', function(d) {
         return d.parent ? 1.0 : 1.0;
      });
      
    // ********** REMOVE NODES **********
    var nodeExit = node.exit()    
    nodeExit
      .transition()
      .duration(duration)
      .attr("transform", function() {
        return `translate(${source.y + source.width + source.expandWidth}, ${source.x})`;
      })
      .remove()
      .on('start', () => {
        this.svg.renderingProcess++;
      })
      .on('end', () => {
        this.svg.renderingProcess--;
      })

    // On exit reduce the node circles size to 0
    nodeExit.select('rect.node-rect').transition().duration(duration).attr('rx', 0).attr('width', 0);
    nodeExit.select('text.node-text').transition().duration(duration * 0.5).style('fill-opacity', 0.0);
    nodeExit.select('rect.node-percent').transition().duration(duration * 0.5).style('fill-opacity', 0.0);
    nodeExit.select('text.node-percent-text').transition().duration(duration * 0.5).style('fill-opacity', 0.0);
    nodeExit.select('image.node-image').remove();
    nodeExit.select('image.expand-image').remove();
    nodeExit.select('image.collapse-image').remove();

    // Update the links...
    var link = this.svg.selectAll('path.link').data(links, function(d) { return d.id; });

    // Enter any new links at the parent's previous position.
    var linkEnter = link.enter().insert('path', "g")
      .attr("id", function(d) {
        return `link-${d.data.uuid}`;
      })
      .attr('class', (d) => {
        return d.parent ? `link` : `link root`;
      })
      .attr('stroke-width', function(d){
        let percent = Math.round(d.data.countPercent*100);
        if(percent == 0 || percent == 1) {
          return 1;
        }
        else if(percent == 100) {
          return 2;
        }
        else{
          return percent/10;
        }
      })
      .attr('d', function(){
        var o = {x: source.x0, y: source.y0 + source.width + source.expandWidth}
        return diagonal(o, o, false)
      });

    // UPDATE LINKS
    var linkUpdate = linkEnter.merge(link);

    // Transition back to the parent element position
    linkUpdate.transition()
      .duration(duration)
      .attr('d', function(d){ return diagonal(d, d.parent, true) });

    // REMOVE LINKS
    link.exit().transition()
      .duration(duration)
      .attr('d', function() {
        var o = {x: source.x, y: source.y + source.width + source.expandWidth}
        return diagonal(o, o, false)
      })
      .remove();

    // Store the old positions for transition.
    nodes.forEach(function(d){
      d.x0 = d.x;
      d.y0 = d.y;
    });

    // Creates a curved (diagonal) path from parent to the child nodes
    function diagonal(s, d, isUpdate) {
      let dy = d.y;
      if(isUpdate) {
        dy = d.y+d.width;
      }
      
      let path = `M ${s.y} ${s.x}
        C ${(s.y + dy) / 2} ${s.x},
          ${(s.y + dy) / 2} ${d.x},
          ${dy} ${d.x} L ${dy-20} ${d.x}`
      return path
    }

    // get rendered svg size
    this.svg.treeMinY = nodes.reduce(function(minX, el) {return el.x<minX ? el.x : minX;}, 0) - 15
    this.svg.treeMaxY = nodes.reduce(function(maxX, el) {return el.x>=maxX ? el.x : maxX;}, 0) + 15
    if(this.svg && this.svg.node()){
      let detectSvgBox = setInterval(() => {
        let box = this.svg.node().getBBox();
        if(box) {
          this.svg.treeWidth = box.width;
          this.svg.treeHeight = box.height;
          clearInterval(detectSvgBox);
        }
      }, 500)
    }
  }
}
</script>